"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "@zapier/i18n";
import {
  Alert,
  AlertDescription,
  Checkbox,
  Spinner,
  Text,
} from "@zapier/design-system";
import {
  useCreateFolderInTargetAccount,
  useCurrentUser,
  useDeleteFolderInTargetAccount,
  useListFoldersForTargetAccount,
} from "@zapier/client";

import { FolderFilter } from "../../folders/MoveModals/core/FolderFilter";
import {
  getCreateFolderDepthDisabledReason,
  getNewFolderDestinationDepth,
  isDestinationDepthAllowed,
  MAX_SUBFOLDER_DEPTH,
  type CreateFolderDepthDisabledReason,
} from "../CopyToAnotherAccountModal/copyToAnotherAccountUtils";
import {
  buildFolderTree,
  collectNewlyCreatedDescendants,
  filterFolderTree,
  isFolderOwnedByUser,
  type FolderTreeNode,
  type NewlyCreatedFolder,
} from "./folderTree";
import { FolderRow } from "./FolderRow";
import { NewFolderRow } from "./NewFolderRow";
import styles from "./FolderPickerModal.module.css";

interface Props {
  /** Target account whose folders we list. */
  targetAccountId: string;
  /** Currently selected folder id (controlled by parent). */
  selectedFolderId: string | null;
  /** Called when the user selects a folder (real or newly-created). */
  onSelectedFolderIdChange: (folderId: string | null) => void;
  /** Reports the selected folder name for review-step copy text. */
  onSelectedFolderNameChange?: (folderName?: string) => void;
  /** Show root-copy checkbox only when source contains folders. */
  showCopyAsTopLevelFolderOption: boolean;
  /** When true, copy should land at account root (no destination parent). */
  copyAsTopLevelFolder: boolean;
  onCopyAsTopLevelFolderChange: (checked: boolean) => void;
  /**
   * Called whenever inline-create/delete state changes that affects whether
   * the parent's "Continue to copy" button should be enabled. The parent
   * combines this with `selectedFolderId` to gate Continue.
   */
  onMutatingChange?: (isMutating: boolean) => void;
  /**
   * Imperative trigger from the parent's "+ Create new folder" footer button.
   * The parent mounts this content and renders the create button itself; when
   * pressed it calls this callback. Returning a function lets the parent
   * inspect whether a new folder is currently being created/edited so it can
   * disable the button while one is pending.
   */
  registerStartNewFolder?: (start: () => void) => void;
  /**
   * Reports whether there is currently a pending new folder being typed in
   * (so the parent can disable Continue / Create-new-folder).
   */
  onPendingNewChange?: (hasPending: boolean) => void;
  /**
   * Reports whether creating a new folder under the current selection is
   * disabled by depth rules, plus the reason for tooltip copy.
   */
  onCreateNewFolderDisabledChange?: (payload: {
    isDisabled: boolean;
    reason?: CreateFolderDepthDisabledReason;
  }) => void;
  /**
   * Reports whether the currently-selected destination folder is valid for
   * copy depth rules. Parent uses this to gate Continue.
   */
  onSelectedDestinationValidityChange?: (isValid: boolean) => void;
  /** Source subtree depth for selected folders, where selected source folder is depth 1. */
  sourceSubtreeMaxDepth?: number;
  /** Whether the source selection includes at least one folder subtree. */
  hasFolderSource: boolean;
  /**
   * Called when loading the target account's folders fails, with the BFF's
   * classified failure `reason` (e.g. `"saml_mismatch"`, else `"unknown"`).
   * Fires at most once per `(targetAccountId, reason)` so the parent can log
   * the disabled state without double-counting query retries.
   */
  onTargetFoldersError?: (reason: string) => void;
}

/**
 * Read the BFF's classified failure reason off a folder-list query error.
 * The reason is spread into `error.data` by the tRPC errorFormatter; it isn't
 * part of the inferred error type, so we read it defensively and default to
 * `"unknown"`.
 */
function readTargetFoldersErrorReason(error: unknown): string {
  const reason = (error as { data?: { reason?: unknown } } | null)?.data
    ?.reason;
  return typeof reason === "string" ? reason : "unknown";
}

interface PendingNewFolder {
  /** Temporary client-only id while the user is still typing the name. */
  localId: string;
  parentId: string | null;
}

interface InlineErrorState {
  title: string;
  message: string;
}

function toFriendlyFolderMutationError(parameters: {
  error: unknown;
  permissionFallback: string;
  genericFallback: string;
}): string {
  const { error, permissionFallback, genericFallback } = parameters;
  if (!(error instanceof Error)) return genericFallback;
  const raw = error.message?.trim();
  if (!raw) return genericFallback;
  const normalized = raw.toLowerCase();
  if (
    normalized === "forbidden" ||
    normalized === "access denied" ||
    normalized.includes("forbidden")
  ) {
    return permissionFallback;
  }
  return raw;
}

/**
 * Body-only content for the "Select a destination folder" step of the
 * single-modal cross-account copy flow. The parent (`CopyToAnotherAccountModal`)
 * owns the `ModalDialog`, header, and footer (including the
 * "+ Create new folder" / Cancel / Continue buttons).
 *
 * Folder lifecycle is unchanged from the previous standalone modal:
 *  - Existing folders are listed via `useListFoldersForTargetAccount`.
 *  - Inline create commits to the BFF immediately on name confirm so the
 *    new folder is real before the copy call.
 *  - Inline delete cascades through newly-created descendants leaves-first.
 */
export function FolderPickerModal({
  targetAccountId,
  selectedFolderId,
  onSelectedFolderIdChange,
  onSelectedFolderNameChange,
  showCopyAsTopLevelFolderOption,
  copyAsTopLevelFolder,
  onCopyAsTopLevelFolderChange,
  onMutatingChange,
  registerStartNewFolder,
  onPendingNewChange,
  onCreateNewFolderDisabledChange,
  onSelectedDestinationValidityChange,
  sourceSubtreeMaxDepth,
  hasFolderSource,
  onTargetFoldersError,
}: Props) {
  const t = useTranslations("copy-to-another-account-modal");

  const {
    folders,
    isLoading,
    error: listError,
  } = useListFoldersForTargetAccount(targetAccountId);
  const { createFolderInTargetAccount, isCreating } =
    useCreateFolderInTargetAccount();
  const { deleteFolderInTargetAccount, isDeleting } =
    useDeleteFolderInTargetAccount();
  const { user } = useCurrentUser();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterOwnedByMe, setFilterOwnedByMe] = useState(false);
  const [newFolders, setNewFolders] = useState<NewlyCreatedFolder[]>([]);
  const [pendingNew, setPendingNew] = useState<PendingNewFolder | undefined>();
  const [inlineError, setInlineError] = useState<
    InlineErrorState | undefined
  >();
  const isCommittingNewFolderReference = useRef(false);
  // Expand/collapse state — start with *every* folder collapsed. When the user
  // clicks a chevron we toggle membership here; when
  // they create a new folder or re-enter this step with a nested selection,
  // we auto-add the relevant parent ids so the target is visible.
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(
    () => new Set<string>(),
  );

  const isMutating = isCreating || isDeleting;

  // Report a target-account folder-load failure to the parent exactly once per
  // (targetAccountId, reason) so the disabled state is logged without
  // double-counting query retries or re-renders.
  const reportedFoldersErrorKeyReference = useRef<string | undefined>(
    undefined,
  );
  useEffect(() => {
    if (!listError) {
      reportedFoldersErrorKeyReference.current = undefined;
      return;
    }
    const reason = readTargetFoldersErrorReason(listError);
    const key = `${targetAccountId}:${reason}`;
    if (reportedFoldersErrorKeyReference.current === key) return;
    reportedFoldersErrorKeyReference.current = key;
    onTargetFoldersError?.(reason);
  }, [listError, targetAccountId, onTargetFoldersError]);

  useEffect(() => {
    onMutatingChange?.(isMutating);
  }, [isMutating, onMutatingChange]);

  useEffect(() => {
    onPendingNewChange?.(!!pendingNew);
  }, [pendingNew, onPendingNewChange]);

  const flatTree = useMemo(
    () => buildFolderTree(folders, newFolders),
    [folders, newFolders],
  );

  const selectedNode = useMemo(
    () => flatTree.find((node) => node.id === selectedFolderId),
    [flatTree, selectedFolderId],
  );

  const selectedDestinationDepth = selectedNode
    ? selectedNode.depth + 1
    : undefined;

  useEffect(() => {
    if (!selectedFolderId) {
      onSelectedFolderNameChange?.();
      return;
    }
    onSelectedFolderNameChange?.(selectedNode?.name);
  }, [selectedFolderId, selectedNode, onSelectedFolderNameChange]);

  const isSelectedDestinationDepthAllowed = useMemo(() => {
    if (!selectedFolderId || !selectedDestinationDepth) return true;
    return isDestinationDepthAllowed({
      hasFolderSource,
      sourceSubtreeMaxDepth,
      destinationDepth: selectedDestinationDepth,
      maxSubfolderDepth: MAX_SUBFOLDER_DEPTH,
    });
  }, [
    selectedFolderId,
    selectedDestinationDepth,
    hasFolderSource,
    sourceSubtreeMaxDepth,
  ]);

  useEffect(() => {
    onSelectedDestinationValidityChange?.(isSelectedDestinationDepthAllowed);
  }, [isSelectedDestinationDepthAllowed, onSelectedDestinationValidityChange]);

  // The "Owned by me" filter is only meaningful when we know the current
  // user's numeric id (mirrors the Move-to-folder modal).
  const ownedByMeUserId =
    filterOwnedByMe && typeof user?.id === "number" ? user.id : undefined;
  const isFilterActive =
    searchQuery.trim().length > 0 || ownedByMeUserId !== undefined;

  // Ids that pass the active search/ownership filters (match + ancestors).
  const filteredVisibleIds = useMemo(
    () => filterFolderTree(flatTree, { searchQuery, ownedByMeUserId }),
    [flatTree, searchQuery, ownedByMeUserId],
  );

  // `parentId → childIds` index. Doubles as a "has children" check (any
  // folder that appears here is a parent) and drives the visibility filter
  // below. While a filter is active we only count children that survive the
  // filter, so a node shows an interactive chevron only when expanding it
  // would actually reveal something.
  const childIdsByParentId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const node of flatTree) {
      if (isFilterActive && !filteredVisibleIds.has(node.id)) continue;
      const parentId = node.parentId;
      if (!parentId) continue;
      if (!map.has(parentId)) map.set(parentId, []);
      map.get(parentId)!.push(node.id);
    }
    return map;
  }, [flatTree, isFilterActive, filteredVisibleIds]);

  // `childId → parentId`, used to walk ancestors when auto-expanding toward
  // the currently selected folder.
  const parentIdByChildId = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of flatTree) {
      if (node.parentId) map.set(node.id, node.parentId);
    }
    return map;
  }, [flatTree]);

  // When a user re-enters this step (via "back" from review) with a nested
  // destination still selected, make sure its ancestor chain is expanded so
  // the selection stays visible in the list.
  useEffect(() => {
    if (!selectedFolderId) return;
    const ancestors: string[] = [];
    let cursor = parentIdByChildId.get(selectedFolderId);
    while (cursor) {
      ancestors.push(cursor);
      cursor = parentIdByChildId.get(cursor);
    }
    if (ancestors.length === 0) return;
    setExpandedFolderIds((previous) => {
      const next = new Set(previous);
      for (const id of ancestors) next.add(id);
      return next;
    });
  }, [selectedFolderId, parentIdByChildId]);

  // Reveal matches when a filter becomes active (or its inputs change) by
  // expanding only the ancestors needed to surface them — never the matched
  // folders themselves. Expand/collapse still applies on top (see
  // `visibleTree`), so the chevrons stay interactive and no matched folder is
  // left expanded once the filter is cleared. Runs only when the filter set
  // changes, so a manual collapse sticks.
  useEffect(() => {
    if (!isFilterActive) return;
    setExpandedFolderIds((previous) => {
      let changed = false;
      const next = new Set(previous);
      for (const id of filteredVisibleIds) {
        let cursor = parentIdByChildId.get(id);
        while (cursor) {
          if (!next.has(cursor)) {
            next.add(cursor);
            changed = true;
          }
          cursor = parentIdByChildId.get(cursor);
        }
      }
      return changed ? next : previous;
    });
  }, [isFilterActive, filteredVisibleIds, parentIdByChildId]);

  // Visibility: a node is shown only when every ancestor on its path to the
  // root is currently expanded (top-level roots are always visible). While a
  // filter is active the node must also survive the filter. Honoring the
  // expand/collapse state in both modes keeps the chevrons working when a
  // filter such as "Owned by me" is on.
  const visibleTree = useMemo(() => {
    return flatTree.filter((node) => {
      if (isFilterActive && !filteredVisibleIds.has(node.id)) return false;
      let cursor: string | undefined = node.parentId ?? undefined;
      while (cursor) {
        if (!expandedFolderIds.has(cursor)) return false;
        cursor = parentIdByChildId.get(cursor);
      }
      return true;
    });
  }, [
    flatTree,
    isFilterActive,
    filteredVisibleIds,
    expandedFolderIds,
    parentIdByChildId,
  ]);

  // When "Owned by me" is on, a previously-selected folder the user doesn't
  // own can no longer be a valid destination. Clear it so the parent's
  // Continue gating stays correct (matches the Move modal's behavior). Search
  // is transient and intentionally preserves the selection.
  useEffect(() => {
    if (ownedByMeUserId === undefined || !selectedNode) return;
    if (!isFolderOwnedByUser(selectedNode, ownedByMeUserId)) {
      // eslint-disable-next-line unicorn/no-null
      onSelectedFolderIdChange(null);
    }
  }, [ownedByMeUserId, selectedNode, onSelectedFolderIdChange]);

  const rootDestinationNodes = useMemo(
    () => flatTree.filter((node) => node.depth === 0),
    [flatTree],
  );

  const areAllRootDestinationsDepthDisabled = useMemo(
    () =>
      showCopyAsTopLevelFolderOption &&
      rootDestinationNodes.length > 0 &&
      rootDestinationNodes.every((node) => {
        const destinationDepth = node.depth + 1;
        return !isDestinationDepthAllowed({
          hasFolderSource,
          sourceSubtreeMaxDepth,
          destinationDepth,
          maxSubfolderDepth: MAX_SUBFOLDER_DEPTH,
        });
      }),
    [
      showCopyAsTopLevelFolderOption,
      rootDestinationNodes,
      hasFolderSource,
      sourceSubtreeMaxDepth,
    ],
  );

  const createNewFolderDisabledReason = useMemo(
    () =>
      getCreateFolderDepthDisabledReason({
        selectedParentDepth: selectedDestinationDepth,
        hasFolderSource,
        sourceSubtreeMaxDepth,
        maxSubfolderDepth: MAX_SUBFOLDER_DEPTH,
      }),
    [selectedDestinationDepth, hasFolderSource, sourceSubtreeMaxDepth],
  );

  useEffect(() => {
    onCreateNewFolderDisabledChange?.({
      isDisabled: createNewFolderDisabledReason !== undefined,
      reason: createNewFolderDisabledReason,
    });
  }, [createNewFolderDisabledReason, onCreateNewFolderDisabledChange]);

  // If every root destination is depth-blocked, selecting any folder destination
  // would be invalid. Auto-enable "copy as top-level folder" so the user can proceed.
  useEffect(() => {
    if (!areAllRootDestinationsDepthDisabled || copyAsTopLevelFolder) return;
    onCopyAsTopLevelFolderChange(true);
  }, [
    areAllRootDestinationsDepthDisabled,
    copyAsTopLevelFolder,
    onCopyAsTopLevelFolderChange,
  ]);

  function getParentDepth(parentId: string | null): number | undefined {
    if (!parentId) return undefined;
    const parent = flatTree.find((node) => node.id === parentId);
    return parent ? parent.depth + 1 : undefined;
  }

  const handleToggleExpanded = (folderId: string) => {
    setExpandedFolderIds((previous) => {
      const next = new Set(previous);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const handleStartNewFolder = () => {
    if (pendingNew) return;
    if (createNewFolderDisabledReason) return;
    // Switching to "create new folder" means we're no longer browsing existing
    // folders. Clear an active search so the selected parent (which the search
    // may have filtered out of view) and the new-folder input are visible.
    if (searchQuery) setSearchQuery("");
    setPendingNew({
      localId: `pending-${Date.now()}`,
      // `null` means create at root level (bottom of the tree list).
      // eslint-disable-next-line unicorn/no-null
      parentId: selectedFolderId ?? null,
    });
    // Auto-expand the parent so the input is actually visible (the parent
    // could be in collapsed-by-default state).
    if (selectedFolderId) {
      setExpandedFolderIds((previous) => {
        const next = new Set(previous);
        next.add(selectedFolderId);
        return next;
      });
    }
  };

  // Expose start-new-folder to the parent footer button.
  useEffect(() => {
    registerStartNewFolder?.(handleStartNewFolder);
    // We intentionally re-register only when the imperative function semantics
    // change (selectedFolderId determines the parent of the next new folder,
    // pendingNew determines whether we no-op, and searchQuery determines
    // whether we clear an active filter so the parent stays visible).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    registerStartNewFolder,
    selectedFolderId,
    pendingNew,
    createNewFolderDisabledReason,
    searchQuery,
  ]);

  const handleCommitNewFolder = async (rawName: string) => {
    if (!pendingNew || isCommittingNewFolderReference.current) return;
    isCommittingNewFolderReference.current = true;
    const name = rawName.trim();
    if (!name) {
      setPendingNew(undefined);
      isCommittingNewFolderReference.current = false;
      return;
    }
    setInlineError(undefined);
    try {
      const result = await createFolderInTargetAccount({
        targetAccountId,
        name,
        parentId: pendingNew.parentId,
      });
      setNewFolders((previous) => [
        ...previous,
        {
          id: result.folderId,
          name,
          parentId: pendingNew.parentId,
          isNew: true,
        },
      ]);
      // Expand the parent so the freshly-created folder is visible in the
      // (otherwise collapsed-by-default) tree.
      if (pendingNew.parentId) {
        const parentIdToExpand = pendingNew.parentId;
        setExpandedFolderIds((previous) => {
          const next = new Set(previous);
          next.add(parentIdToExpand);
          return next;
        });
      }
      const newDestinationDepth = getNewFolderDestinationDepth({
        selectedParentDepth: getParentDepth(pendingNew.parentId),
      });
      const canSelectAsDestination = isDestinationDepthAllowed({
        hasFolderSource,
        sourceSubtreeMaxDepth,
        destinationDepth: newDestinationDepth,
        maxSubfolderDepth: MAX_SUBFOLDER_DEPTH,
      });

      if (canSelectAsDestination) {
        onSelectedFolderIdChange(result.folderId);
      } else {
        setInlineError({
          title: t("folder-step.errors.create-title"),
          message: t("folder-step.errors.create-too-deep-for-copy", {
            maxDepth: MAX_SUBFOLDER_DEPTH,
          }),
        });
      }
    } catch (error) {
      setInlineError({
        title: t("folder-step.errors.create-title"),
        message: toFriendlyFolderMutationError({
          error,
          permissionFallback: t("folder-step.errors.create-no-permission"),
          genericFallback: t("folder-step.errors.create-failed"),
        }),
      });
    } finally {
      setPendingNew(undefined);
      isCommittingNewFolderReference.current = false;
    }
  };

  const handleDeleteNewFolder = async (folderId: string) => {
    setInlineError(undefined);
    const descendantsDeepestFirst = collectNewlyCreatedDescendants(
      folderId,
      newFolders,
    );
    try {
      await deleteFolderInTargetAccount({
        targetAccountId,
        folderId,
        childFolderIds: descendantsDeepestFirst,
      });
      const removed = new Set([folderId, ...descendantsDeepestFirst]);
      setNewFolders((previous) => previous.filter((f) => !removed.has(f.id)));
      if (selectedFolderId && removed.has(selectedFolderId)) {
        // eslint-disable-next-line unicorn/no-null
        onSelectedFolderIdChange(null);
      }
    } catch {
      setInlineError({
        title: t("folder-step.errors.delete-title"),
        message: t("folder-step.errors.delete-failed"),
      });
    }
  };

  const showOwnedByMeFilter = typeof user?.id === "number";
  // When every destination is depth-blocked, "copy as top level folder" is
  // forced on and no folder is selectable, so search / ownership filters can
  // never produce a usable result. Hide them to avoid dead controls.
  const showFilters = !areAllRootDestinationsDepthDisabled;
  const showNoResults =
    !isLoading &&
    !listError &&
    isFilterActive &&
    visibleTree.length === 0 &&
    !pendingNew;

  return (
    <div className={styles.body}>
      <Text type="Body2" tag="p" className={styles.description}>
        {t("folder-step.description")}
      </Text>

      {showFilters && (
        <div className={styles.folderFilterRow}>
          <FolderFilter
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            clearSearch={() => setSearchQuery("")}
          />
          {showOwnedByMeFilter && (
            <Checkbox
              isSelected={filterOwnedByMe}
              value="owned-by-me"
              name="folder-picker-owned-by-me"
              onChange={() => setFilterOwnedByMe((previous) => !previous)}
            >
              {t("folder-step.owned-by-me")}
            </Checkbox>
          )}
        </div>
      )}

      <div className={styles.treeList}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="medium" aria-label={t("folder-step.loading")} />
          </div>
        ) : listError ? (
          <Text type="Body2" tag="p" className={styles.errorText}>
            {readTargetFoldersErrorReason(listError) === "saml_mismatch"
              ? t("folder-step.errors.load-blocked-sso")
              : (listError.message ?? t("folder-step.errors.load-failed"))}
          </Text>
        ) : showNoResults ? (
          <Text type="Body2" tag="p" color="TextWeaker">
            {t("folder-step.no-results")}
          </Text>
        ) : (
          renderTreeWithPendingInput({
            visibleTree,
            pendingNew,
            selectedFolderId,
            childIdsByParentId,
            expandedFolderIds,
            isMutating,
            onSelectedFolderIdChange,
            onToggleExpanded: handleToggleExpanded,
            onDelete: handleDeleteNewFolder,
            onCommitNewFolder: handleCommitNewFolder,
            onCancelNewFolder: () => setPendingNew(undefined),
            newFolderPlaceholder: t("folder-step.new-folder-placeholder"),
            selectDisabledTooltip: t("folder-step.select-disabled-depth", {
              maxDepth: MAX_SUBFOLDER_DEPTH,
            }),
            sourceSubtreeMaxDepth,
            hasFolderSource,
            maxSubfolderDepth: MAX_SUBFOLDER_DEPTH,
          })
        )}
      </div>

      {showCopyAsTopLevelFolderOption ? (
        <div className={styles.topLevelCheckboxRow}>
          <Checkbox
            isSelected={copyAsTopLevelFolder}
            isDisabled={areAllRootDestinationsDepthDisabled}
            value="copy-as-top-level-folder"
            name="folder-picker-copy-as-top-level"
            onChange={(nextChecked) => {
              onCopyAsTopLevelFolderChange(nextChecked);
              if (nextChecked) {
                // eslint-disable-next-line unicorn/no-null
                onSelectedFolderIdChange(null);
              }
            }}
          >
            {t("folder-step.copy-as-top-level-folder")}
          </Checkbox>
        </div>
      ) : undefined}

      {inlineError ? (
        <Alert status="error" title={inlineError.title}>
          <AlertDescription>{inlineError.message}</AlertDescription>
        </Alert>
      ) : undefined}
    </div>
  );
}

interface RenderTreeArguments {
  visibleTree: FolderTreeNode[];
  pendingNew: PendingNewFolder | undefined;
  selectedFolderId: string | null;
  childIdsByParentId: Map<string, string[]>;
  expandedFolderIds: Set<string>;
  isMutating: boolean;
  onSelectedFolderIdChange: (folderId: string | null) => void;
  onToggleExpanded: (id: string) => void;
  onDelete: (id: string) => void;
  onCommitNewFolder: (name: string) => Promise<void> | void;
  onCancelNewFolder: () => void;
  newFolderPlaceholder: string;
  selectDisabledTooltip: string;
  sourceSubtreeMaxDepth?: number;
  hasFolderSource: boolean;
  maxSubfolderDepth: number;
}

/**
 * Render the visible folder rows + (when set) the pending-new-folder input
 * inline at its parent's "last child" slot. This keeps
 * placement: when you click "+ Create new folder" with a folder selected,
 * the input appears under that folder's children, not at the bottom of the
 * whole tree.
 *
 * Position math: in our DFS-flattened `visibleTree`, all descendants of a
 * given parent are contiguous immediately after that parent. We walk
 * forward from the parent's index until we hit a node at `<= parent.depth`,
 * and insert the input there. With no parent (root-level new folder), the
 * input goes at the very end of the list.
 */
function renderTreeWithPendingInput({
  visibleTree,
  pendingNew,
  selectedFolderId,
  childIdsByParentId,
  expandedFolderIds,
  isMutating,
  onSelectedFolderIdChange,
  onToggleExpanded,
  onDelete,
  onCommitNewFolder,
  onCancelNewFolder,
  newFolderPlaceholder,
  selectDisabledTooltip,
  sourceSubtreeMaxDepth,
  hasFolderSource,
  maxSubfolderDepth,
}: RenderTreeArguments) {
  let pendingInsertIndex = -1;
  let pendingDepth = 0;
  if (pendingNew) {
    if (pendingNew.parentId) {
      const parentIndex = visibleTree.findIndex(
        (n) => n.id === pendingNew.parentId,
      );
      if (parentIndex === -1) {
        // Defensive: the parent isn't currently visible (e.g. hidden by an
        // active filter). Append the input at the end instead of silently
        // dropping it. In practice `handleStartNewFolder` clears the search
        // first, so the parent is visible by the time we render here.
        pendingInsertIndex = visibleTree.length;
        pendingDepth = 0;
      } else {
        const parentDepth = visibleTree[parentIndex]!.depth;
        let cursor = parentIndex + 1;
        while (
          cursor < visibleTree.length &&
          visibleTree[cursor]!.depth > parentDepth
        ) {
          cursor++;
        }
        pendingInsertIndex = cursor;
        pendingDepth = parentDepth + 1;
      }
    } else {
      pendingInsertIndex = visibleTree.length;
      pendingDepth = 0;
    }
  }

  const items: React.ReactNode[] = [];
  for (const [index, node] of visibleTree.entries()) {
    const destinationDepth = node.depth + 1;
    const isDepthAllowed = isDestinationDepthAllowed({
      hasFolderSource,
      sourceSubtreeMaxDepth,
      destinationDepth,
      maxSubfolderDepth,
    });
    const isDisabled = !isDepthAllowed;
    items.push(
      <FolderRow
        key={node.id}
        node={node}
        isSelected={selectedFolderId === node.id}
        isDisabled={isDisabled}
        disabledTooltip={selectDisabledTooltip}
        hasChildren={childIdsByParentId.has(node.id)}
        isExpanded={expandedFolderIds.has(node.id)}
        onSelect={(id) => {
          // Toggle: clicking the already-selected folder deselects,
          // matching the folder row behavior.
          if (isDisabled) return;
          // eslint-disable-next-line unicorn/no-null
          onSelectedFolderIdChange(selectedFolderId === id ? null : id);
        }}
        onToggleExpanded={onToggleExpanded}
        onDelete={onDelete}
        isMutating={isMutating}
      />,
    );
    if (pendingNew && index + 1 === pendingInsertIndex) {
      items.push(
        <NewFolderRow
          key={`pending-${pendingNew.localId}`}
          depth={pendingDepth}
          onCommit={onCommitNewFolder}
          onCancel={onCancelNewFolder}
          placeholder={newFolderPlaceholder}
        />,
      );
    }
  }
  // Edge case: empty tree + a root-level pending input.
  if (pendingNew && pendingInsertIndex === 0 && visibleTree.length === 0) {
    items.push(
      <NewFolderRow
        key={`pending-${pendingNew.localId}`}
        depth={pendingDepth}
        onCommit={onCommitNewFolder}
        onCancel={onCancelNewFolder}
        placeholder={newFolderPlaceholder}
      />,
    );
  }
  return items;
}
